import {
  LEGACY_STRATEGIES,
  isNoAuthStrategy,
  PROVIDER_DOCS_SLUG,
} from './constants';
import { providerForToolKey } from '@cotera/api';

// Shared with the reusable tool components; re-exported here so this folder
// keeps one import surface for tool display helpers.
export {
  generateToolMentionHtml,
  getAuthStrategyLabel,
  formatStrategyName,
} from '@cotera/client/app/components/tools/utils';

// Terms that should be uppercased in credential field labels
const CREDENTIAL_TERM_CASING: Record<string, string> = {
  api: 'API',
  url: 'URL',
  id: 'ID',
  oauth: 'OAuth',
  jwt: 'JWT',
};

// Check if a strategy is legacy (either in the explicit list or contains "legacy" in name)
export const isLegacyStrategy = (name: string): boolean =>
  LEGACY_STRATEGIES.includes(name) || name.toLowerCase().includes('legacy');

/**
 * Gets the documentation URL for a provider.
 * Returns null if the provider doesn't have public documentation.
 */
export const getProviderDocsUrl = (provider: string): string | null => {
  // Check if provider has special handling
  if (provider in PROVIDER_DOCS_SLUG) {
    const slug = PROVIDER_DOCS_SLUG[provider];
    if (slug === null) {
      return null;
    }
    return `https://cotera.co/docs/reference/tools/individual-tools/${slug}`;
  }
  // Default: use provider key directly
  return `https://cotera.co/docs/reference/tools/individual-tools/${provider}`;
};

/**
 * Strips a leading provider name from a tool name, so a row can show the
 * action under a provider subtitle instead of repeating the provider twice.
 * e.g. "Slack Send Message" + "Slack" -> "Send Message".
 */
export function extractToolAction(
  name: string,
  providerDisplayName: string
): string {
  if (
    name.toLowerCase().startsWith(providerDisplayName.toLowerCase()) &&
    name.length > providerDisplayName.length
  ) {
    return name.slice(providerDisplayName.length).trim();
  }
  return name;
}

// Apply proper casing to credential field text
export function formatCredentialText(text: string): string {
  let result = text;
  for (const [term, replacement] of Object.entries(CREDENTIAL_TERM_CASING)) {
    // Match the term case-insensitively as a whole word
    const regex = new RegExp(`\\b${term}\\b`, 'gi');
    result = result.replace(regex, replacement);
  }
  return result;
}

// Filter strategies for display - remove legacy, identify no-auth strategies
export function filterStrategies<T extends { name: string; toolCount: number }>(
  strategies: T[]
): {
  displayStrategies: T[];
  isNoAuth: boolean;
} {
  const filtered = strategies.filter((s) => !isLegacyStrategy(s.name));

  // Check if ALL strategies are no-auth types (not just when there's exactly one)
  const isNoAuth =
    filtered.length > 0 && filtered.every((s) => isNoAuthStrategy(s.name));

  // Filter out no-auth strategies from display since they're handled separately
  const displayStrategies = filtered.filter((s) => !isNoAuthStrategy(s.name));

  return { displayStrategies, isNoAuth };
}

/**
 * Extracts the provider from a tool key.
 * Tool keys are in format "provider/group/action" (e.g., "github/repos/createBranch"),
 * except where the key's namespace is not the provider — see `providerForToolKey`.
 */
export const getProviderFromToolKey = (toolKey: string): string =>
  providerForToolKey(toolKey);
